import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';
import { LuArrowRight } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { checkupCounts } from './checkupModel';
import { TONES } from '../repairsUI';
import { chartColor } from '../../shared/TimeSeriesChart';
import { cardSurface } from '../../shared/Card';

export const CHECKUP_PATH = '/source-files/checkup';

// A ring: the share of statements that need a look.
function Ring({ part, whole, size }) {
  const stroke = size / 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const share = whole ? Math.min(part / whole, 1) : 0;
  return (
    <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={chartColor('phone.alert')}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${Math.max(share * circumference, 2)} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Flex position="absolute" inset={0} direction="column" align="center" justify="center">
        <Text fontSize={size > 80 ? 'xl' : 'sm'} fontWeight={700} color="redesign.heading" lineHeight={1}>{part}</Text>
      </Flex>
    </Box>
  );
}

function Chip({ children, good }) {
  return (
    <Box
      as="span"
      px={2.5}
      py="3px"
      borderRadius="full"
      fontSize="xs"
      {...TONES[good ? 'green' : 'orange']}
      whiteSpace="nowrap"
    >
      {children}
    </Box>
  );
}

/**
 * The checkup at the top of the Statements view: how many statements need
 * a look and why, and "Start checkup". Nothing when none does. `phone`
 * draws it as the phone's card.
 */
export default function CheckupBanner({ flags, phone = false }) {
  const { t } = useTranslation('repairs');
  const navigate = useNavigate();
  const counts = checkupCounts(flags);
  if (!counts.attention) return null;

  const title = t(counts.attention === 1 ? 'checkup.banner.titleOne' : 'checkup.banner.titleOther', { count: counts.attention });
  // "1 doesn't add up" / "2 don't add up": each chip in its count's form.
  const counted = (key, count) => t(`checkup.banner.${key}${count === 1 ? 'One' : 'Other'}`, { count });
  const chips = [
    counts.doesntAddUp > 0 && counted('doesntAddUp', counts.doesntAddUp),
    counts.reprocess > 0 && counted('reprocess', counts.reprocess),
    counts.leftovers > 0 && counted('leftovers', counts.leftovers),
  ].filter(Boolean);
  const start = () => navigate(CHECKUP_PATH);

  if (phone) {
    return (
      <Box mx="16px" mb="12px" p="16px" bg="redesign.bg.surface" border="1px solid" borderColor="redesign.border" borderRadius="16px">
        <Flex align="center" gap="14px" mb="14px">
          <Ring part={counts.attention} whole={counts.total} size={56} />
          <Box minW={0}>
            <Text fontSize="16px" fontWeight={600} color="redesign.heading">{title}</Text>
            <Text fontSize="13px" color="redesign.text.muted">{chips.join(' · ')}</Text>
          </Box>
        </Flex>
        <Button w="100%" h="48px" colorScheme="blue" fontSize="16px" onClick={start}>{t('checkup.banner.start')}</Button>
      </Box>
    );
  }

  return (
    <Flex
      align="center"
      gap={6}
      p={5}
      mb={4}
      {...cardSurface}
      borderRadius="14px"
    >
      <Box textAlign="center">
        <Ring part={counts.attention} whole={counts.total} size={84} />
        <Text fontSize="2xs" color="redesign.text.muted" mt={1}>{t('checkup.banner.of', { count: counts.total })}</Text>
      </Box>
      <Box flex={1} minW={0}>
        <Text as="h2" fontSize="md" fontWeight={600} color="redesign.heading">{title}</Text>
        <Text fontSize="sm" color="redesign.text" mt={1} maxW="640px">{t('checkup.banner.body')}</Text>
        <Flex gap={2} wrap="wrap" mt={2}>
          {chips.map((chip) => <Chip key={chip}>{chip}</Chip>)}
          {counts.fixed > 0 && <Chip good>{counted('fixed', counts.fixed)}</Chip>}
        </Flex>
      </Box>
      <Flex direction="column" align="stretch" gap={1} flexShrink={0}>
        <Button colorScheme="blue" rightIcon={<Icon as={LuArrowRight} />} onClick={start}>{t('checkup.banner.start')}</Button>
        <Text fontSize="2xs" color="redesign.text.muted" textAlign="center">{t('checkup.banner.hint')}</Text>
      </Flex>
    </Flex>
  );
}
